import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, type TextFieldProps } from '@mui/material';
import type { DynamicEntityRow } from '../api/strapiDynamicEntity';
import { useAuth } from '../auth/AuthContext';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { bakuMutusSearchMinLength, useBakuMutusSearchQuery } from '../query/hooks';

const SEARCH_DEBOUNCE_MS = 320;

/** Label opsi dropdown: kode — judul — nama (pencarian server: kode atau judul). */
export function bakuMutuMasterOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const judul = String(row.judul ?? '').trim();
  return [judul].filter(Boolean).join(' — ') || String(row.documentId ?? '').trim();
}

/** Teks disimpan di form / kolom tabel: judul (fallback nama, kode, documentId). */
export function bakuMutuMasterDisplayName(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const judul = String(row.judul ?? '').trim();
  if (judul) return judul;
  const nama = String(row.nama_baku_mutu ?? '').trim();
  if (nama) return nama;
  const kode = String(row.kode ?? '').trim();
  if (kode) return kode;
  return String(row.documentId ?? '').trim();
}

function dedupeBakuRows(rows: DynamicEntityRow[]): DynamicEntityRow[] {
  const seen = new Set<string>();
  const out: DynamicEntityRow[] = [];
  for (const r of rows) {
    const id = String(r.documentId ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

type Props = {
  value: DynamicEntityRow | null;
  onChange: (row: DynamicEntityRow | null) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  inputTestId?: string;
  textFieldProps?: Partial<TextFieldProps>;
};

/** Autocomplete master `baku-mutus`: pencarian server by kode atau judul (Strapi `documentId`). */
export function BakuMutuMasterAutocomplete({
  value,
  onChange,
  disabled = false,
  label = 'Baku mutu',
  required = false,
  inputTestId,
  textFieldProps,
}: Props) {
  const { jwt } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebouncedValue(inputValue, SEARCH_DEBOUNCE_MS);
  const qTrim = debouncedSearch.trim();
  const minLen = bakuMutusSearchMinLength();
  const searchQ = useBakuMutusSearchQuery(jwt, debouncedSearch);

  useEffect(() => {
    if (!value?.documentId?.trim()) {
      setInputValue('');
      return;
    }
    setInputValue(bakuMutuMasterOptionLabel(value));
  }, [value?.documentId, value]);

  const options = useMemo(() => {
    const fromApi = qTrim.length >= minLen ? (searchQ.data?.rows ?? []) : [];
    const merged = [...(value?.documentId ? [value] : []), ...fromApi];
    return dedupeBakuRows(merged);
  }, [value, qTrim.length, minLen, searchQ.data?.rows]);

  const searchLoading = qTrim.length >= minLen && searchQ.isFetching;
  const searchError =
    qTrim.length >= minLen && searchQ.isError
      ? searchQ.error instanceof Error
        ? searchQ.error.message
        : 'Gagal mencari baku mutu.'
      : undefined;

  const hintParts: string[] = [];
  if (qTrim.length > 0 && qTrim.length < minLen) {
    hintParts.push(`Ketik setidaknya ${minLen} karakter (kode atau judul).`);
  } else if (qTrim.length >= minLen && searchLoading) {
    hintParts.push('Mencari…');
  }
  if (searchError) hintParts.push(searchError);
  const searchHint = hintParts.length > 0 ? hintParts.join(' ') : undefined;
  const propHelper = textFieldProps?.helperText;
  const mergedHelper =
    typeof propHelper === 'string' && propHelper.trim()
      ? [propHelper, searchHint].filter(Boolean).join(' ')
      : searchHint;

  return (
    <Autocomplete<DynamicEntityRow, false, false, false>
      options={options}
      loading={searchLoading}
      loadingText="Memuat…"
      filterOptions={(x) => x}
      disabled={disabled || !jwt}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      noOptionsText={
        qTrim.length >= minLen
          ? searchLoading
            ? 'Memuat…'
            : 'Tidak ada baku mutu yang cocok.'
          : value?.documentId
            ? 'Ketik kode atau judul untuk mencari lain.'
            : `Ketik minimal ${minLen} karakter (kode atau judul).`
      }
      getOptionLabel={(o) => bakuMutuMasterOptionLabel(o)}
      isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          required={required}
          size="small"
          fullWidth
          error={Boolean(textFieldProps?.error) || Boolean(searchError && qTrim.length >= minLen)}
          helperText={mergedHelper}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              ...(inputTestId ? { 'data-testid': inputTestId } : {}),
            },
          }}
        />
      )}
    />
  );
}
